import React, {useState} from 'react';
import {Row} from './styles';
import {useNavigate} from 'react-router-dom';
import {NestedKeys} from 'shared/types/key-manipulation';
import Column from '../column';
import {ProjectionOptionProps, ProjectionProps} from '../..';
import getNestedKey from 'shared/util/get-nested-key';
import {translatedProps} from 'shared/util/translated-props';

interface TableRowProps<T> {
  onRowClick?: (data: any) => void;
  data: any;
  projection: ProjectionProps<T>;
  translatedPropsKey: keyof typeof translatedProps;
  reorderRows: (from: string, to: string) => void;
  draggable?: boolean;
}

const TableRow: <T>(p: TableRowProps<T>) => React.ReactElement<T> = ({
  onRowClick,
  translatedPropsKey,
  projection,
  data,
  reorderRows,
  draggable,
}) => {
  const navigation = useNavigate();
  const [isDragOver, setDragOver] = useState(false);

  const getHeaderLabel = (option: NestedKeys | ProjectionOptionProps) => {
    if (typeof option === 'string')
      return getNestedKey(translatedProps[translatedPropsKey], option);
    if (option.label) return option.label;
    return getNestedKey(
      translatedProps[translatedPropsKey],
      (option as any).value,
    );
  };

  const getProjectionOption = (projectionKey: NestedKeys) =>
    projection.options
      .filter((option) => typeof option === 'string' || !option.hidden)
      .find(
        (option) =>
          (typeof option !== 'object' ? option : option.value) ===
          projectionKey,
      )!;

  const getColumnFromOption = (option: ProjectionOptionProps | string) =>
    typeof option === 'string'
      ? option
      : option.column
      ? option.column
      : option.value!;

  return (
    <Row
      {...{isDragOver, draggable}}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDrop={(e) => {
        reorderRows(e.dataTransfer.getData('field'), data.id);
        setDragOver(false);
      }}
      onDragLeave={() => setDragOver(false)}
      onDragStart={(e) => e.dataTransfer.setData('field', data.id)}
      onClick={() => (onRowClick ? onRowClick(data) : navigation(data.id))}>
      {projection?.values &&
        projection.values
          .filter((value) => getProjectionOption(value))
          .map((value, i) => {
            const option = getProjectionOption(value);
            return (
              <Column
                key={i}
                dataLabel={getHeaderLabel(option)}
                {...{column: getColumnFromOption(option), row: data}}
              />
            );
          })}
      {!projection?.values &&
        projection.options.map((option, index) => (
          <Column
            key={index}
            dataLabel={getHeaderLabel(option)}
            {...{column: getColumnFromOption(option), row: data}}
          />
        ))}
    </Row>
  );
};

export default TableRow;
